import React, { useEffect, useRef, useState } from 'react';

const FaceComparisonResult = ({ verification, loading, error }) => {
    console.log('🎨 FaceComparisonResult rendered:', { verification, loading, error });
    
    const sourceImageRef = useRef(null);
    const targetImageRef = useRef(null);
    const [imageDimensions, setImageDimensions] = useState({});
    
    if (verification) {
        console.log('🖼️ Image URLs:', {
            sourceImage: verification?.sourceImageUrl,
            targetImage: verification?.targetImageUrl
        });
    }

    // Calculate actual image dimensions and positioning for bounding boxes
    const calculateImagePosition = (imageElement) => {
        if (!imageElement) return null;
        
        const containerRect = imageElement.getBoundingClientRect();
        const naturalWidth = imageElement.naturalWidth;
        const naturalHeight = imageElement.naturalHeight;
        
        // Calculate how the image fits within the container with object-contain
        const containerAspect = containerRect.width / containerRect.height;
        const imageAspect = naturalWidth / naturalHeight;
        
        let displayWidth, displayHeight, offsetX, offsetY;
        
        if (imageAspect > containerAspect) {
            // Image is wider - fit to width
            displayWidth = containerRect.width;
            displayHeight = containerRect.width / imageAspect;
            offsetX = 0;
            offsetY = (containerRect.height - displayHeight) / 2;
        } else {
            // Image is taller - fit to height
            displayHeight = containerRect.height;
            displayWidth = containerRect.height * imageAspect;
            offsetX = (containerRect.width - displayWidth) / 2;
            offsetY = 0;
        }
        
        return {
            displayWidth,
            displayHeight,
            offsetX,
            offsetY,
            scaleX: displayWidth / naturalWidth,
            scaleY: displayHeight / naturalHeight
        };
    };
    
    if (loading) {
        return (
            <div className="face-verification-container mt-4 p-4 border rounded-lg bg-blue-50">
                <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-blue-600 font-semibold">🔄 Analyzing faces...</p>
                    <p className="text-sm text-gray-600">Comparing user profile with submission</p>
                </div>
            </div>
        );
    }

    if (error || (verification && verification.error)) {
        // Handle different error formats safely
        let errorMessage = 'Face verification failed';
        
        try {
            if (error) {
                // Direct error prop
                if (typeof error === 'string') {
                    errorMessage = error;
                } else if (error && typeof error === 'object') {
                    errorMessage = error.message || 
                                  (error.status ? `Error ${error.status}: ${error.message || 'Unknown error'}` : null) ||
                                  'Face verification error';
                }
            } else if (verification?.error) {
                // Error from verification object
                if (typeof verification.error === 'string') {
                    errorMessage = verification.error;
                } else if (verification.error && typeof verification.error === 'object') {
                    errorMessage = verification.error.message || 
                                  (verification.error.status ? `Error ${verification.error.status}: ${verification.error.message || 'Unknown error'}` : null) ||
                                  'Face verification error';
                }
            }
        } catch (e) {
            console.error('Error parsing face verification error:', e);
            errorMessage = 'Face verification failed';
        }
        
        // Ensure errorMessage is always a string
        if (typeof errorMessage !== 'string') {
            errorMessage = 'Face verification error occurred';
        }
        
        console.log('⚠️ Face verification error:', { error, verificationError: verification?.error, finalMessage: errorMessage });
        const isWarning = verification?.status === 'warning';
        
        return (
            <div className={`face-verification-container mt-4 p-4 border rounded-lg ${isWarning ? 'bg-yellow-50 border-yellow-300' : 'bg-red-50 border-red-300'}`}>
                <div className="text-center py-4">
                    <p className={`font-semibold ${isWarning ? 'text-yellow-700' : 'text-red-600'}`}>
                        {isWarning ? '⚠️ Face Verification Warning' : '❌ Face Verification Failed'}
                    </p>
                    <p className={`text-sm mt-2 ${isWarning ? 'text-yellow-600' : 'text-gray-600'}`}>
                        {errorMessage}
                    </p>
                    {verification?.sourceImageUrl && verification?.targetImageUrl && (
                        <div className="mt-4 text-xs text-gray-500">
                            <p>Images were available but verification could not be completed</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!verification) {
        return null;
    }

    return (
        <div className="face-verification-container mt-4 p-4 border rounded-lg bg-gray-50">
            <h3 className="text-lg font-semibold mb-3 text-center">🔍 Face Verification Analysis</h3>
            
            {/* Side by side images */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div className="text-center">
                    <p className="font-semibold mb-2 text-blue-600">👤 User Profile Photo</p>
                    <p className="text-xs text-gray-600 mb-2">(Baseline for comparison)</p>
                    <div className="relative mx-auto max-w-sm">
                        <img 
                            ref={sourceImageRef}
                            src={verification.sourceImageUrl} 
                            alt="User Profile" 
                            className="w-full h-64 object-contain rounded border-2 border-blue-300 bg-gray-50" 
                            onLoad={() => {
                                setTimeout(() => {
                                    const imagePos = calculateImagePosition(sourceImageRef.current);
                                    if (imagePos) {
                                        setImageDimensions(prev => ({ ...prev, source: imagePos }));
                                    }
                                }, 100);
                            }}
                        />
                        {/* Bounding box overlay for source image if available */}
                        {verification.sourceImageFace?.BoundingBox && imageDimensions.source && (
                            <div 
                                className="absolute border-4 border-cyan-400 shadow-lg pointer-events-none"
                                style={{
                                    left: `${imageDimensions.source.offsetX + (verification.sourceImageFace.BoundingBox.Left * imageDimensions.source.displayWidth)}px`,
                                    top: `${imageDimensions.source.offsetY + (verification.sourceImageFace.BoundingBox.Top * imageDimensions.source.displayHeight)}px`,
                                    width: `${verification.sourceImageFace.BoundingBox.Width * imageDimensions.source.displayWidth}px`,
                                    height: `${verification.sourceImageFace.BoundingBox.Height * imageDimensions.source.displayHeight}px`,
                                    boxShadow: '0 0 0 3px rgba(6, 182, 212, 0.6), inset 0 0 0 2px rgba(255, 255, 255, 0.8)',
                                    borderRadius: '4px'
                                }}
                            >
                                <div className="absolute -top-6 left-0 bg-cyan-500 text-white text-xs px-2 py-1 rounded font-bold">
                                    PROFILE
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="text-center">
                    <p className="font-semibold mb-2 text-green-600">📸 Challenge Submission</p>
                    <p className="text-xs text-gray-600 mb-2">(Photo to be verified)</p>
                    <div className="relative mx-auto max-w-sm">
                        <img 
                            ref={targetImageRef}
                            src={verification.targetImageUrl} 
                            alt="Challenge Submission" 
                            className="w-full h-64 object-contain rounded border-2 border-green-300 bg-gray-50" 
                            onLoad={() => {
                                setTimeout(() => {
                                    const imagePos = calculateImagePosition(targetImageRef.current);
                                    if (imagePos) {
                                        setImageDimensions(prev => ({ ...prev, target: imagePos }));
                                    }
                                }, 100);
                            }}
                        />
                        {/* Bounding box overlay for target image if available */}
                        {verification.faceDetails?.boundingBox && imageDimensions.target && (
                            <div 
                                className="absolute border-4 border-lime-400 shadow-lg pointer-events-none"
                                style={{
                                    left: `${imageDimensions.target.offsetX + (verification.faceDetails.boundingBox.Left * imageDimensions.target.displayWidth)}px`,
                                    top: `${imageDimensions.target.offsetY + (verification.faceDetails.boundingBox.Top * imageDimensions.target.displayHeight)}px`,
                                    width: `${verification.faceDetails.boundingBox.Width * imageDimensions.target.displayWidth}px`,
                                    height: `${verification.faceDetails.boundingBox.Height * imageDimensions.target.displayHeight}px`,
                                    boxShadow: '0 0 0 3px rgba(132, 204, 22, 0.6), inset 0 0 0 2px rgba(255, 255, 255, 0.8)',
                                    borderRadius: '4px'
                                }}
                            >
                                <div className="absolute -top-6 left-0 bg-lime-500 text-white text-xs px-2 py-1 rounded font-bold">
                                    SUBMISSION
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Verification Results */}
            <div className="bg-white p-4 rounded border shadow-sm">
                <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold">Face Match Confidence:</span>
                    <span className={`text-2xl font-bold ${
                        verification.confidence > 80 ? 'text-green-600' : 
                        verification.confidence > 50 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                        {verification.confidence.toFixed(1)}%
                    </span>
                </div>
                
                {/* Confidence meter */}
                <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div 
                        className={`h-3 rounded-full transition-all duration-500 ${
                            verification.confidence > 80 ? 'bg-green-600' : 
                            verification.confidence > 50 ? 'bg-yellow-600' : 'bg-red-600'
                        }`}
                        style={{width: `${Math.min(verification.confidence, 100)}%`}}
                    ></div>
                </div>
                
                {/* Match status */}
                <div className="flex justify-between items-center mb-3">
                    <span className="font-semibold">Verification Status:</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                        verification.confidence > 80 ? 'bg-green-100 text-green-800' : 
                        verification.confidence > 50 ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-red-100 text-red-800'
                    }`}>
                        {verification.confidence > 80 ? '✅ VERIFIED' : 
                         verification.confidence > 50 ? '⚠️ REVIEW NEEDED' : '❌ NOT VERIFIED'}
                    </span>
                </div>
                
                {/* Auto-suggested points */}
                <div className="flex justify-between items-center border-t pt-3">
                    <span className="font-semibold">Auto-Suggested Points:</span>
                    <span className="text-xl font-bold text-blue-600">{verification.suggestedPoints}</span>
                </div>
                
                {/* Additional details */}
                {verification.faceDetails && (
                    <div className="mt-3 text-sm text-gray-600 border-t pt-2">
                        <p>🎯 Face detected in submission with {verification.faceDetails.confidence?.toFixed(1)}% confidence</p>
                        {verification.faceDetails.quality && (
                            <p>📊 Image quality: Brightness {verification.faceDetails.quality.Brightness?.toFixed(1)}, Sharpness {verification.faceDetails.quality.Sharpness?.toFixed(1)}</p>
                        )}
                    </div>
                )}
                
                {/* AWS Response info */}
                <div className="mt-2 text-xs text-gray-500">
                    <p>✨ Analysis powered by AWS Rekognition</p>
                </div>
            </div>
        </div>
    );
};

export default FaceComparisonResult;